import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import {
  BITE_STATUS,
  useBiteStatusStore,
} from "@/features/tutor/store/useBiteStatusStore";
import { useAssessmentStore } from "@/features/tutor/store/useAssessmentStore";
import { isAssessmentSlideType } from "@/features/tutor/constants/assessmentTypes";

export type LessonCompleteVariant = "celebrate" | "quiet";
export type LessonCompleteOverride = "default" | LessonCompleteVariant;

export const LESSON_COMPLETION_THRESHOLD = 0.5;

export interface LessonCompletionInputs {
  bitesCompleted: number;
  totalBites: number;
  hasEndCheck: boolean;
  endCheckPercentAnswered: number | null;
}

export interface LessonCompletionReading {
  inputs: LessonCompletionInputs;
  ready: boolean;
}

interface SlideLike {
  slide_index?: number;
  slide_type?: string;
  metadata?: { slide_type?: string };
}

function slideType(slide: SlideLike | null | undefined): string | undefined {
  return slide?.metadata?.slide_type ?? slide?.slide_type;
}

function isContentSlide(slide: SlideLike | null | undefined): boolean {
  return slide != null && !isAssessmentSlideType(slideType(slide));
}

function endsWithCustomPage(slides: SlideLike[]): boolean {
  const lastNonExitTicket = slides
    .filter((s) => slideType(s) !== "exit_ticket")
    .pop();
  return slideType(lastNonExitTicket) === "custom";
}

interface AssessmentSliceLike {
  has?: boolean;
  attemptId?: unknown;
  completed?: boolean;
  percentAnswered?: number | null;
}

function wasOpened(slice: AssessmentSliceLike | null | undefined): boolean {
  return (
    slice?.attemptId != null ||
    slice?.completed === true ||
    slice?.percentAnswered != null
  );
}

const RECORDED_AS_NOT_COMPLETED = new Set<string>([
  BITE_STATUS.INCOMPLETE,
  BITE_STATUS.SKIPPED_BY_USER,
  BITE_STATUS.SKIPPED_BY_AI,
]);

export function isLessonCompleted({
  bitesCompleted,
  totalBites,
  hasEndCheck,
  endCheckPercentAnswered,
}: LessonCompletionInputs): boolean {
  const biteClause =
    totalBites > 0
      ? bitesCompleted / totalBites >= LESSON_COMPLETION_THRESHOLD
      : true;
  if (!biteClause) return false;
  if (!hasEndCheck) return true;
  return (endCheckPercentAnswered ?? 0) >= LESSON_COMPLETION_THRESHOLD;
}

export function readLessonCompletionInputs(): LessonCompletionReading {
  const slideState = useSlideStore.getState();
  const slides: SlideLike[] = slideState.slides ?? [];
  const { statuses, isInitialized } = useBiteStatusStore.getState();
  const assessments = useAssessmentStore.getState().assessments;
  const exitTicket = assessments?.exit_ticket;
  const custom = assessments?.custom;

  const contentSlides = slides.filter(isContentSlide);
  const completedIndices = new Set(
    contentSlides
      .filter((s) => statuses[s.slide_index!] === BITE_STATUS.COMPLETED)
      .map((s) => s.slide_index!),
  );

  const currentIndex = slideState.currentSlideIndex;
  const lastContentIndex = contentSlides.length
    ? contentSlides[contentSlides.length - 1].slide_index
    : undefined;
  if (
    slideState.hasResolvedSlideIndex &&
    lastContentIndex != null &&
    currentIndex === lastContentIndex &&
    !RECORDED_AS_NOT_COMPLETED.has(statuses[currentIndex])
  ) {
    completedIndices.add(currentIndex);
  }

  const endCheck =
    exitTicket?.has === true
      ? exitTicket
      : custom?.has === true && endsWithCustomPage(slides) && wasOpened(custom)
        ? custom
        : null;

  return {
    ready: slides.length > 0 && isInitialized,
    inputs: {
      bitesCompleted: completedIndices.size,
      totalBites: contentSlides.length,
      hasEndCheck: endCheck != null,
      endCheckPercentAnswered: endCheck?.percentAnswered ?? null,
    },
  };
}

interface SurveyQuestionLike {
  isEmpty: () => boolean;
}
interface SurveyModelLike {
  getAllQuestions: (visibleOnly?: boolean) => SurveyQuestionLike[];
}

export function computePercentAnswered(model: SurveyModelLike): number {
  const questions = model.getAllQuestions(true);
  if (questions.length === 0) return 1;
  const answered = questions.filter((q) => !q.isEmpty()).length;
  return answered / questions.length;
}

export function normalizeLessonCompleteVariant(
  value: unknown,
): LessonCompleteVariant | null {
  if (value === "celebrate" || value === "quiet") return value;
  return null;
}

export function resolveLessonCompleteVariant(
  debugOverride?: unknown,
): LessonCompleteVariant {
  const override = normalizeLessonCompleteVariant(debugOverride);
  if (override) return override;

  const { inputs, ready } = readLessonCompletionInputs();
  if (!ready) return "quiet";
  return isLessonCompleted(inputs) ? "celebrate" : "quiet";
}

/**
 * Every content bite completed -- No expectation mode's bar for finishing a
 * lesson (100%), not the 50% the celebration uses. Counted the same way, the
 * bite on screen included unless it was recorded as not completed.
 */
export function everyBiteCompleted(): boolean {
  const { inputs, ready } = readLessonCompletionInputs();
  return (
    ready && inputs.totalBites > 0 && inputs.bitesCompleted >= inputs.totalBites
  );
}
